import get from 'lodash/get';
import pick from 'lodash/pick';
import map from 'lodash/map';
import some from 'lodash/some';
import sumBy from 'lodash/sumBy';
import castArray from 'lodash/castArray';
import moment from 'moment-timezone';
import { prop, pipe, equals, either } from 'ramda';
import config from '../../../config';
import { fullName } from '../../common/helpers/users';
import { applyAppointmentDiscount } from '../../main/settings/discounts/helpers';
import { APPOINTMENT_PAYMENT_STATUS } from '../../main/appointments/constants';

export const paymentTypes = {
  STRIPE: 'stripe',
  PAYFAC: 'squire',
  CASH: 'cash',
  CARD: 'card',
  READER: 'reader',
  GIFT_CARD: 'gift_card',
};

const bookedViaSquirePaymentTypes = [paymentTypes.STRIPE, paymentTypes.PAYFAC];

const tippingWindow = 2;

export function formatAppointment(appointment) {
  const customerLN =
    get(appointment, 'customer.lastName') ? ` ${get(appointment, 'customer.lastName', '').slice(0, 1)}.` : '';
  const customerTitleName = get(appointment, 'customer.firstName') + customerLN;
  const serviceNames = map(appointment.appointment_service, 'name').join(', ');
  const appointmentData = pick(appointment, [
    'id',
    'customerNotes',
    'customer',
    'barberId',
    'groupId',
    'shopId',
    'status',
    'appointment_service',
    'duration',
    'customDuration',
    'tipAmount',
    'payment',
    'paidInShop',
    'createdAt',
    'bookedBy',
    'rescheduledBy',
    'canceledBy',
    'noShow',
    'bookedWithAnyBarber',
    'bookedFromWaitingList',
    'bookedWithMembership',
    'platform',
    'isRecurring',
    'recurrEveryNumDays',
    'recurrUntil',
    'isNewBarberCustomer',
    'isNewShopCustomer',
    'retainedBarberCustomer',
    'totalCost',
    'discount',
    'doNotChargeTax',
    'promos',
    'autoChargeRecurring',
    'refunds',
    'refundedAmount',
    'maxRefundAmount',
    'taxAmount',
    'giftCardId',
    'giftCardAmount',
    'updatedAt',
    'paymentTypes',
    'paymentType',
    'paymentStatus',
    'errors',
    'barberReady',
    'waiver',
    'waiverViewLink',
    'costs',
    'shop',
    'canBeCancelled',
    'canBeModified',
  ]);
  if (appointment.barber) {
    appointmentData.barberFullName = fullName(appointment.barber);
  }
  if (appointment.promos) {
    appointmentData.isSquirePromo = get(appointment, 'promos[0].customer_promo.promo.isSquirePromo');
    appointmentData.promoAmount = 0;
    appointmentData.promoCode = '';
    if (!appointmentData.isSquirePromo) {
      appointmentData.promoAmount = appointment.promos.reduce((sum, item) => sum + item.promoAmount, 0);
      appointmentData.promoCode = get(appointment, 'promos[0].customer_promo.promo.code', '');
    }
  }

  appointmentData.discountAmount = get(appointmentData, 'discount.amount', 0);

  const customer = {
    ...appointment.customer,
    deleted: !get(appointment, 'customer.shops', []).map(shop => shop.id).includes(appointment.shopId),
  };

  return {
    ...appointmentData,
    customer,
    start: appointment.dateTime,
    end: appointment.endTime,
    customerFullName: fullName(appointment.customer),
    title: customerTitleName,
    description: serviceNames,
  };
}

export function extractAppointments(appointments) {
  return map(appointments, appointment => formatAppointment(appointment));
}

export const isCardOrPosPayment = payment =>
  payment && (payment.paymentType === paymentTypes.CARD || payment.paymentType === paymentTypes.READER);

export const isCardPayment = (appointment) => {
  const apptPaymentTypes = castArray(appointment.paymentTypes);

  return paymentTypes.CARD === appointment.paymentType || apptPaymentTypes.includes(paymentTypes.CARD);
};

export const isReaderPayment = (appointment) => {
  const apptPaymentTypes = castArray(appointment.paymentTypes);

  return paymentTypes.READER === appointment.paymentType || apptPaymentTypes.includes(paymentTypes.READER);
};

export const isCardOrReaderPayment = (appointment) => {
  const apptPaymentTypes = castArray(appointment.paymentTypes);
  return [paymentTypes.CARD, paymentTypes.READER].includes(appointment.paymentType) ||
         apptPaymentTypes.includes(paymentTypes.CARD) ||
         apptPaymentTypes.includes(paymentTypes.READER);
};

export const isGiftCardPayment = (appointment) => {
  const apptPaymentTypes = castArray(appointment.paymentTypes);

  return paymentTypes.GIFT_CARD === appointment.paymentType || apptPaymentTypes.includes(paymentTypes.GIFT_CARD);
};

const isBookedViaSquirePaymentType = paymentType => bookedViaSquirePaymentTypes.includes(paymentType);

export const isBookedViaSquire = (appointment) => {
  const apptPaymentTypes = [appointment.paymentType, ...castArray(appointment.paymentTypes)];

  return apptPaymentTypes.some(paymentType => isBookedViaSquirePaymentType(paymentType));
};

export const isSquireAppointment = appointment => isBookedViaSquirePaymentType(get(appointment, 'payment.paymentType'));

export const isNotSquireAndCardOrPosPaid = appointment =>
  appointment && !isSquireAppointment(appointment) && isCardOrReaderPayment(appointment);

export function isPaidOnline(payment) {
  return payment && payment.processed && payment.chargeId && payment.successful;
}

export function isReserved(payment) {
  return payment && payment.successful && !payment.processed && isBookedViaSquirePaymentType(payment.paymentType);
}

export function isPaidOnlineFailed(payment) {
  return payment && payment.processed && !payment.successful;
}

export function isPaidCash(payment) {
  return payment && payment.successful && payment.paymentType === paymentTypes.CASH;
}

export function isPaid(appointment) {
  const payment = appointment.payment;
  return payment && payment.processed && payment.successful && payment.chargeId;
}

export const isCharged = appointment => get(appointment, 'payment.captured');
export const isDepositOrPrepaid = appointment => get(appointment, 'payment.isDeposit');

export function isWithinTippingWindow(appointment) {
  const timezone = get(appointment, 'shop.timezone');
  const endDate = moment(appointment.end).tz(timezone).add(tippingWindow, 'hours');
  const now = moment().tz(timezone);
  return now.isBefore(endDate);
}

export const getAppointmentPaymentStatus = prop('paymentStatus');
export const isAppointmentFullyRefunded = pipe(
  getAppointmentPaymentStatus,
  equals(APPOINTMENT_PAYMENT_STATUS.refunded),
);
export const isAppointmentPartiallyRefunded = pipe(
  getAppointmentPaymentStatus,
  equals(APPOINTMENT_PAYMENT_STATUS.partiallyRefunded),
);
export const isAppointmentRefunded = either(
  isAppointmentFullyRefunded,
  isAppointmentPartiallyRefunded,
);

export function isLastRecurring(day, lastDate) {
  return day.isSame(lastDate, 'minute');
}

export function changedTime(day, time, appointment, timezone) {
  const formatCompare = `${config.dateFormatISO} HH:mm`;
  const formatDisplay = 'ddd, MMM Do @ h:mm A';
  const oldTime = moment(appointment.start).tz(timezone).format(formatCompare);
  const newTime = `${day} ${time}`;
  return {
    oldTime: moment(oldTime).format(formatDisplay),
    newTime: moment(newTime).format(formatDisplay),
    isTimeChanged: !moment(oldTime).isSame(newTime),
  };
}

export function countTotalCost(services) {
  if (!services || !services.length) {
    return 0;
  }
  return services.reduce((result, service) => result + service.cost, 0);
}

export const countTotalCostWithTaxes = services => sumBy(services, 'costWithTaxes') || 0;
export const countTotalCostWithoutTaxes = services => sumBy(services, 'costWithoutTaxes') || 0;
export const countTotalTaxes = services => sumBy(services, 'taxAmount') || 0;
export const countTotalCostWithDiscount = (services, discount, promoAmount = 0) =>
  applyAppointmentDiscount(countTotalCostWithoutTaxes(services), discount) - promoAmount;
export const countCostWithTaxes = services => sumBy(services, 'cost') || 0;
export function countTotalDuration(services, defaultValue = 0) {
  // Returns defaultValue instead of 0 for cases if services were not selected
  // but we need use default value for service length (for example barber.minServiceLen
  // or just config.defaultMinServiceLen if barber was not selected too)
  if (!services || !services.length) {
    return defaultValue;
  }
  const sum = services.reduce((a, b) => ({
    duration: a.duration + b.duration,
  }), {
    duration: 0,
  });
  return parseInt(sum.duration, 10);
}

export function makeServicesArray(selectedServices, appointment) {
  return selectedServices.map((service) => {
    const serviceId = service.serviceId || service.id;
    const serviceFromAppointment = get(appointment, 'appointment_service', [])
        .find(({ service: { id } }) => id === serviceId);

    return serviceFromAppointment ? { id: serviceFromAppointment.id } : { serviceId };
  });
}

export function haveAppointmentsOnDate(barber, appointments, currentDate, timezone) {
  const timezonedDate = moment.tz(currentDate, timezone);
  return some(appointments, appointment =>
    timezonedDate.isSame(appointment.start, 'day') && appointment.barberId === barber.id
  );
}

export const getGiftCardId = prop('giftCardId');
